import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { Store } from '@ngxs/store';
import { JWTService } from '@smatchy/ionic-auth';
import { environment } from '../environments/environment';
import { AUTH_RECOVERY_RETRIED, SKIP_AUTH_RECOVERY } from './auth-recovery.context';
import { RefreshTokenAction } from './core/domains/user/state/auth.actions';

/**
 * Attaches the API token to outgoing requests, and renews it when the API says
 * it has expired.
 *
 * Before this, the app renewed its token in exactly one place: LoggedComponent,
 * on launch, and only if getMe() had already succeeded. So an expired token had
 * no recovery path. getMe() answered 401, the error branch dispatched
 * DisconnectUserAction, and the user was sent back to the sign-in screen with
 * no explanation. That was survivable only because API tokens lasted a year and
 * so rarely expired in practice.
 *
 * Now that the lifetime is 30 days, a user who does not open the app for a
 * month would hit exactly that. Hence this: on a 401, renew once and replay the
 * request. The user sees nothing.
 *
 * Three things worth knowing before changing this:
 *
 * - The renewal goes through RefreshTokenAction rather than calling the API
 *   directly, so the NGXS state and JWTService stay in step with the new token.
 *   That action asks Firebase for a fresh ID token (forceRefresh) and trades it
 *   at POST /auth/refresh. Firebase refresh tokens do not expire on their own,
 *   so this works as long as the Firebase session has not been revoked.
 *
 * - POST /auth/refresh carries no JwtAuthGuard on the API side. It authenticates
 *   on the Firebase ID token in the body, not on the Bearer header. An expired
 *   API token therefore cannot lock us out of renewing it.
 *
 * - Renewal is single-flight. Several requests fire at launch and would all 401
 *   together; they share one refresh instead of triggering one each.
 *
 * When renewal fails, the original 401 is propagated untouched rather than
 * forcing a sign-out here. Callers that already treat a 401 as "session over",
 * LoggedComponent among them, keep behaving exactly as they do today.
 *
 * RENEWING AHEAD OF TIME
 *
 * Recovering from a 401 works, but it lets the burst happen first: ten
 * requests leaving together on an expired token get ten 401s, one renewal, and
 * ten replays. Production runs on Cloud Run with at most one instance and a
 * concurrency of 80, so that burst is a real share of a low ceiling. Suggested
 * by Huong on 8 September 2026.
 *
 * So before sending, the interceptor reads the token's own expiry (the `exp`
 * claim, no signature check, the API does that) and renews first when it is
 * due within RENEW_BEFORE_EXPIRY_MS. The 401 recovery above stays as the safety
 * net for what reading the claim cannot predict: a device clock that is wrong,
 * a token revoked on the server, a renewal that fails.
 *
 * A proactive renewal is attempted at most once per token. A device clock far
 * ahead of the real time would otherwise see every token as expiring and renew
 * before every single request.
 */
/**
 * How long before its expiry a token is renewed. Five minutes: long enough that
 * a request prepared on a slow connection, or an app resumed from the
 * background, does not leave with a token that expires in flight; short enough
 * that it costs one renewal a few minutes early in a thirty-day lifetime.
 */
export const RENEW_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * The `exp` claim of a JWT, in milliseconds, or null when the token cannot be
 * read as one. Nothing is verified here: the API checks the signature, this
 * only decides whether to renew early.
 */
export function tokenExpiry(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')));
    return typeof json?.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  /** Set while a renewal is in flight, so concurrent 401s share it. */
  private renewal$: Observable<string> | null = null;

  /** The last token a proactive renewal was attempted for, whatever its outcome. */
  private proactivelyRenewedToken: string | null = null;

  /**
   * Store is resolved lazily, never in the constructor. It pulls in AuthState,
   * which injects HttpClient, which builds the interceptor chain: asking for it
   * here would be a cyclic dependency and Angular would refuse to start.
   */
  constructor(private readonly authService: JWTService, private readonly injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldHandle(request.url) === false) {
      return next.handle(request);
    }

    const token = this.authService.getAccessToken();
    if (this.shouldRenewAhead(request, token)) {
      this.proactivelyRenewedToken = token;
      return this.renewAccessToken().pipe(
        // A failed proactive renewal changes nothing: the request leaves with
        // the token it had, and the 401 path decides what happens next.
        catchError(() => of(token)),
        switchMap((usableToken) => this.sendWithRecovery(request, next, usableToken)),
      );
    }

    return this.sendWithRecovery(request, next, token);
  }

  private sendWithRecovery(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string | null,
  ): Observable<HttpEvent<unknown>> {
    const authorized = this.withToken(request, token);

    return next.handle(authorized).pipe(
      catchError((error: unknown) => {
        if (!this.shouldRecover(request, error)) {
          return throwError(() => error);
        }

        return this.renewAccessToken().pipe(
          switchMap((freshToken) =>
            next.handle(
              this.withToken(request, freshToken, {
                context: request.context.set(AUTH_RECOVERY_RETRIED, true),
              }),
            ),
          ),
          // Renewal failed: the Firebase session is gone, or the API refused.
          // Surface the original 401, not the refresh error, so callers keep
          // seeing the failure they already know how to handle.
          catchError(() => throwError(() => error)),
        );
      }),
    );
  }

  private shouldRenewAhead(request: HttpRequest<unknown>, token: string | null): boolean {
    if (!token || request.context.get(SKIP_AUTH_RECOVERY) || this.isAuthRoute(request.url)) {
      return false;
    }
    if (token === this.proactivelyRenewedToken) {
      // Already tried for this token. Requests that arrive while that renewal
      // is still in flight join it rather than leave with the old token, which
      // is the burst this exists to prevent; once it has finished, never again.
      return this.renewal$ !== null;
    }
    const expiresAt = tokenExpiry(token);
    return expiresAt !== null && expiresAt - Date.now() < RENEW_BEFORE_EXPIRY_MS;
  }

  private renewAccessToken(): Observable<string> {
    if (!this.renewal$) {
      const store = this.injector.get(Store);

      this.renewal$ = store.dispatch(new RefreshTokenAction('request')).pipe(
        map(() => {
          const freshToken = this.authService.getAccessToken();
          if (!freshToken) {
            throw new Error('Token renewal completed without producing a token');
          }
          return freshToken;
        }),
        finalize(() => {
          this.renewal$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.renewal$;
  }

  private shouldRecover(request: HttpRequest<unknown>, error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }
    if (request.context.get(SKIP_AUTH_RECOVERY) || request.context.get(AUTH_RECOVERY_RETRIED)) {
      return false;
    }
    // Renewing is pointless on the routes that hand out tokens in the first
    // place, and retrying /auth/refresh through the refresh path would loop.
    if (this.isAuthRoute(request.url)) {
      return false;
    }
    // No token means the user is not signed in. A 401 is the correct answer.
    return !!this.authService.getAccessToken();
  }

  private isAuthRoute(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/sign-up') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/session')
    );
  }

  private withToken(
    request: HttpRequest<unknown>,
    token: string | null,
    extra: { context?: HttpRequest<unknown>['context'] } = {},
  ): HttpRequest<unknown> {
    return request.clone({
      ...extra,
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  shouldHandle(url: string) {
    const baseUrl = environment.api;
    if (!baseUrl) {
      return false;
    }

    // if request is not for api, do not intercept
    return url.startsWith(baseUrl) === true;
  }
}
